import React, { useState, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const MS_STEPS = ["Auth Provider", "Azure / Microsoft", "Striven API", "General", "Review"];
const GOOGLE_STEPS = ["Auth Provider", "Striven API", "General", "Google Connect", "Review"];

export default function SetupWizard({ onComplete }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [authProvider, setAuthProvider] = useState(null); // "microsoft" | "google"
  const [form, setForm] = useState({
    orgName: "",
    // Microsoft
    azureClientId: "",
    azureClientSecret: "",
    azureTenantId: "",
    sharepointSiteId: "",
    // Google
    googleClientId: "",
    googleClientSecret: "",
    googleDriveFolderId: "",
    googleAppName: "",
    // Shared
    strivenClientId: "",
    strivenClientSecret: "",
    pdfEmail: "",
  });

  const STEPS = authProvider === "google" ? GOOGLE_STEPS : MS_STEPS;
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

const canAdvance = () => {
  if (step === 0) return authProvider !== null && form.orgName.length > 1;

  if (authProvider === "microsoft") {
    if (step === 1) return (
      form.azureClientId.length > 30 &&
      form.azureClientSecret.length > 20 &&
      form.azureTenantId.length > 30
    );
    if (step === 2) return form.strivenClientId.length > 5 && form.strivenClientSecret.length > 10;
    if (step === 3) return form.pdfEmail.includes("@") && form.pdfEmail.includes(".");
  }

  if (authProvider === "google") {
    if (step === 1) return form.strivenClientId.length > 5 && form.strivenClientSecret.length > 10;
    if (step === 2) return form.pdfEmail.includes("@") && form.pdfEmail.includes(".");
    if (step === 3) return (
      form.googleClientId.length > 10 &&
      form.googleClientSecret.length > 10 &&
      form.googleAppName.length > 1 &&
      form.googleDriveFolderId.length > 5 &&
      googleConnected
    );
  }

  return true;
};
const [googleConnected, setGoogleConnected] = useState(false);

useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  if (params.get("setup") === "google-connected") {
    // Restore saved wizard state
    const saved = localStorage.getItem("setupWizardState");
    if (saved) {
      const { form: savedForm, authProvider: savedProvider, step: savedStep } = JSON.parse(saved);
      setForm(savedForm);
      setAuthProvider(savedProvider);
      setStep(savedStep);  // puts them back on step 1
      localStorage.removeItem("setupWizardState");
    }

    setGoogleConnected(true);
    window.history.replaceState({}, "", window.location.pathname);
    return;
  }
}, []);
const SETUP_TOKEN = process.env.REACT_APP_SETUP_TOKEN;

const handleGoogleConnect = async () => {
  localStorage.setItem("setupWizardState", JSON.stringify({ form, authProvider, step }));
  const slug = localStorage.getItem("orgSlug");

  try {
    await axios.post(`${API_BASE_URL}/api/setup/save-google-credentials`, {
      googleClientId: form.googleClientId,
      googleClientSecret: form.googleClientSecret,
      googleAppName: form.googleAppName,
      googleDriveFolderId: form.googleDriveFolderId,
      orgName: form.orgName,
      strivenClientId: form.strivenClientId,
      strivenClientSecret: form.strivenClientSecret,
      pdfEmail: form.pdfEmail,
    }, {
      headers: {
        "X-Org-Slug": slug,
        "X-Setup-Token": SETUP_TOKEN,  // 👈 add this
      }
    });

    const { data } = await axios.get(`${API_BASE_URL}/api/setup/google-auth-url`, {
      headers: { "X-Org-Slug": slug }
    });
    window.location.href = data.url;
  } catch (err) {
    alert(err.response?.data?.error || err.message);
  }
};
const handleSubmit = async () => {
  setSaving(true);
  const slug = localStorage.getItem("orgSlug");
  try {
    await axios.post(`${API_BASE_URL}/api/setup/initialize`, {
      ...form,
      authProvider,
    }, {
      headers: {
        "X-Org-Slug": slug,
        "X-Setup-Token": SETUP_TOKEN,  // 👈 add this
      }
    });
    onComplete();
  } catch (err) {
    alert(err.response?.data?.error || "Setup failed. Please check your credentials.");
  } finally {
    setSaving(false);
  }
};

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>

        {/* Progress bar */}
        <div style={styles.progressBar}>
          {STEPS.map((label, i) => (
            <div key={i} style={styles.stepIndicator}>
              <div style={{ ...styles.dot, background: i <= step ? "#0078d4" : "#ddd" }}>
                {i < step ? "✓" : i + 1}
              </div>
              <span style={{ fontSize: 12, color: i <= step ? "#0078d4" : "#999" }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Step titles */}
<h2 style={styles.title}>
  {step === 0 && "Organization Setup"}
  {step === 1 && authProvider === "microsoft" && "Set up Microsoft / Azure"}
  {step === 1 && authProvider === "google" && "Set up Striven"}
  {step === 2 && authProvider === "microsoft" && "Set up Striven"}
  {step === 2 && authProvider === "google" && "General Settings"}
  {step === 3 && authProvider === "microsoft" && "General Settings"}
  {step === 3 && authProvider === "google" && "Connect Google Account"}
  {step === 4 && "Review & Confirm"}
</h2>

        {/* Step 0 — org name + provider choice */}
        {step === 0 && (
          <div style={styles.fields}>
            <Field label="Organization Name" value={form.orgName} onChange={set("orgName")} />
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8, marginTop: 16 }}>
              Sign-in Provider
            </label>
            <div style={styles.providerRow}>
              <button
                style={{ ...styles.providerBtn, ...(authProvider === "microsoft" ? styles.providerBtnActive : {}) }}
                onClick={() => setAuthProvider("microsoft")}
                type="button"
              >
                <MicrosoftIcon />
                Microsoft
              </button>
              <button
                style={{ ...styles.providerBtn, ...(authProvider === "google" ? styles.providerBtnActive : {}) }}
                onClick={() => setAuthProvider("google")}
                type="button"
              >
                <GoogleIcon />
                Google Workspace
              </button>
            </div>
          </div>
        )}

{/* Step 1 — Microsoft: Azure */}
{step === 1 && authProvider === "microsoft" && (
  <div style={styles.fields}>
    <Field label="Azure Client ID" value={form.azureClientId} onChange={set("azureClientId")} />
    <Field label="Azure Client Secret" value={form.azureClientSecret} onChange={set("azureClientSecret")} type="password" />
    <Field label="Azure Tenant ID" value={form.azureTenantId} onChange={set("azureTenantId")} />
    <Field label="SharePoint Site ID" value={form.sharepointSiteId} onChange={set("sharepointSiteId")} />
  </div>
)}

{/* Step 1 (Google) / Step 2 (Microsoft) — Striven */}
{((step === 1 && authProvider === "google") || (step === 2 && authProvider === "microsoft")) && (
  <div style={styles.fields}>
    <Field label="Striven Client ID" value={form.strivenClientId} onChange={set("strivenClientId")} />
    <Field label="Striven Client Secret" value={form.strivenClientSecret} onChange={set("strivenClientSecret")} type="password" />
  </div>
)}

{/* Step 2 (Google) / Step 3 (Microsoft) — General */}
{((step === 2 && authProvider === "google") || (step === 3 && authProvider === "microsoft")) && (
  <div style={styles.fields}>
    <Field label="PDF Delivery Email" value={form.pdfEmail} onChange={set("pdfEmail")} type="email" />
  </div>
)}
{step === 3 && authProvider === "google" && (
  <div style={styles.fields}>
    <Field label="Google OAuth Client ID" value={form.googleClientId} onChange={set("googleClientId")} />
    <Field label="Google OAuth Client Secret" value={form.googleClientSecret} onChange={set("googleClientSecret")} type="password" />
    <Field label="Application Name" value={form.googleAppName} onChange={set("googleAppName")} />
    <Field label="Google Drive Folder ID" value={form.googleDriveFolderId} onChange={set("googleDriveFolderId")}
      hint="The string at the end of your folder's URL" />

    <div style={{ background: "#f0f7ff", border: "1px solid #bcd", borderRadius: 8, padding: 14, marginBottom: 14 }}>
      <p style={{ fontWeight: 600, fontSize: 13, margin: "0 0 6px" }}>
        Add this redirect URI to your Google OAuth client before connecting:
      </p>
      <code style={{ display: "block", background: "#fff", padding: "8px 10px", borderRadius: 5, fontSize: 12, border: "1px solid #ccc", wordBreak: "break-all" }}>
        {`${API_BASE_URL}/api/setup/google-callback`}
      </code>
      <button type="button" style={{ marginTop: 8, fontSize: 12, padding: "4px 10px", cursor: "pointer" }}
        onClick={() => navigator.clipboard.writeText(`${API_BASE_URL}/api/setup/google-callback`)}>
        Copy
      </button>
    </div>

    <div style={{ marginTop: 16 }}>
      {googleConnected ? (
        <div style={{ color: "#16a34a", fontWeight: 600, fontSize: 14 }}>✓ Google account connected</div>
      ) : (
        <button
          type="button"
          style={{ ...styles.nextBtn, background: "#fff", color: "#333", border: "1px solid #ccc" }}
          onClick={handleGoogleConnect}
          disabled={!form.googleClientId || !form.googleClientSecret}
        >
          <GoogleIcon /> Connect Google Account
        </button>
      )}
    </div>
  </div>
)}
        {/* Step 4 — Review */}
        {step === 4 && (
          <div style={styles.review}>
            <ReviewRow label="Organization Name" value={form.orgName} />
            <ReviewRow label="Auth Provider" value={authProvider === "microsoft" ? "Microsoft" : "Google Workspace"} />
            {authProvider === "microsoft" && <>
              <ReviewRow label="Azure Client ID" value={form.azureClientId} />
              <ReviewRow label="Azure Tenant ID" value={form.azureTenantId} />
              <ReviewRow label="SharePoint Site ID" value={form.sharepointSiteId || "—"} />
            </>}
            {authProvider === "google" && <>
              <ReviewRow label="Google Client ID" value={form.googleClientId} />
              <ReviewRow label="Application Name" value={form.googleAppName} />
              <ReviewRow label="Drive Folder ID" value={form.googleDriveFolderId} />
              <ReviewRow
  label="Google Account Connection"
  value={googleConnected ? "Connected" : "Not Connected"}
/>
            </>}
            <ReviewRow label="Striven Client ID" value={form.strivenClientId} />
            <ReviewRow label="PDF Email" value={form.pdfEmail} />
            <p style={{ color: "#666", fontSize: 13, marginTop: 12 }}>
              Secrets are not shown. Click <strong>Finish</strong> to save and proceed to sign-in.
            </p>
          </div>
        )}

        <div style={styles.actions}>
          {step > 0 && (
            <button style={styles.backBtn} onClick={() => setStep((s) => s - 1)}>
              Back
            </button>
          )}
          {step < STEPS.length - 1 && (
            <button
              style={{ ...styles.nextBtn, opacity: canAdvance() ? 1 : 0.5 }}
              disabled={!canAdvance()}
              onClick={() => setStep((s) => s + 1)}
            >
              Next
            </button>
          )}
          {step === STEPS.length - 1 && (
            <button style={styles.nextBtn} onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving…" : "Finish & Sign In"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", hint }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      {hint && <p style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{hint}</p>}
      <input type={type} value={value} onChange={onChange} style={styles.input} />
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #eee" }}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#444" }}>{value}</span>
    </div>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" style={{ marginRight: 8 }}>
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 48 48" style={{ marginRight: 8 }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}

const styles = {
  overlay: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f3f4f6" },
  card: { background: "#fff", borderRadius: 12, padding: 36, width: 520, boxShadow: "0 4px 24px rgba(0,0,0,0.1)" },
  title: { margin: "0 0 24px", fontSize: 20 },
  fields: {},
  input: { width: "100%", padding: "8px 12px", border: "1px solid #ccc", borderRadius: 6, fontSize: 14, boxSizing: "border-box" },
  progressBar: { display: "flex", justifyContent: "space-between", marginBottom: 28 },
  stepIndicator: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  dot: { width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 700 },
  actions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 28 },
  nextBtn: { background: "#0078d4", color: "#fff", border: "none", borderRadius: 6, padding: "10px 22px", cursor: "pointer", fontSize: 14 },
  backBtn: { background: "#eee", color: "#333", border: "none", borderRadius: 6, padding: "10px 22px", cursor: "pointer", fontSize: 14 },
  providerRow: { display: "flex", gap: 12 },
  providerBtn: { flex: 1, padding: "12px 16px", border: "2px solid #ddd", borderRadius: 8, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600 },
  providerBtnActive: { borderColor: "#0078d4", background: "#f0f7ff" },
  review: {},
};